// frontend/src/components/ImageVersionHistory.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Paper
} from '@mui/material';
import { format } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import apiClient from '../api/client';

const ImageVersionHistory = ({ imageId, currentVersionId }) => {
  const [versions, setVersions] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    if (imageId) {
      fetchVersions();
      fetchSubmissionHistory();
    }
  }, [imageId]);

  const fetchVersions = async () => {
    try {
      const response = await apiClient.get(`/images/${imageId}/versions`);
      setVersions(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching versions:', err);
      setError('Failed to load version history');
      setLoading(false);
    }
  };

  const fetchSubmissionHistory = async () => {
    try {
      const response = await apiClient.get(`/images/${imageId}/submission-history`);
      setSubmissionHistory(response.data);
    } catch (err) {
      console.error('Error fetching submission history:', err);
    }
  };

  const handleActivateVersion = async (versionId) => {
    try {
      await apiClient.post(`/images/${imageId}/versions/${versionId}/activate`);
      // Refresh the page or update parent component
      window.location.reload();
    } catch (err) {
      console.error('Error activating version:', err);
      setError('Failed to activate version');
    }
  };

  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setViewDialogOpen(true);
  };

  const getVersionBadgeColor = (version) => {
    if (version.id === currentVersionId) return 'primary';
    if (version.is_active) return 'success';
    return 'default';
  };

  const getSubmissionForVersion = (versionId) => {
    return submissionHistory.find(sub => sub.image_version_id === versionId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <HistoryIcon sx={{ mr: 1 }} />
        <Typography variant="h6">Version History</Typography>
      </Box>

      {versions.length === 0 ? (
        <Typography color="text.secondary">No version history available</Typography>
      ) : (
        <List>
          {versions.map((version, index) => {
            const submission = getSubmissionForVersion(version.id);
            const isCurrent = version.id === currentVersionId;
            
            return (
              <React.Fragment key={version.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2">
                          Version {version.version_number}
                        </Typography>
                        {isCurrent && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                        {version.badges && version.badges.length > 0 && (
                          <Box display="flex" gap={0.5}>
                            {version.badges.map(badge => (
                              <Chip
                                key={badge}
                                label={badge.replace(/_/g, ' ')}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Created: {format(new Date(version.created_at), 'MMM d, yyyy h:mm a')}
                        </Typography>
                        {version.created_by_email && (
                          <Typography variant="caption" display="block">
                            Created by: {version.created_by_email}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block">
                          Reason: {version.reason}
                        </Typography>
                        {submission && (
                          <Typography variant="caption" display="block" color="success.main">
                            Submitted: {format(new Date(submission.submitted_at), 'MMM d, yyyy h:mm a')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="View version details">
                      <IconButton onClick={() => handleViewVersion(version)} size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {!isCurrent && (
                      <Tooltip title="Make this the current version">
                        <IconButton 
                          onClick={() => handleActivateVersion(version.id)} 
                          size="small"
                          color="primary"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
                {index < versions.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}

      {/* Version Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Version {selectedVersion?.version_number} Details
        </DialogTitle>
        <DialogContent>
          {selectedVersion && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Version Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2">
                  <strong>Created:</strong> {format(new Date(selectedVersion.created_at), 'MMM d, yyyy h:mm a')}
                </Typography>
                {selectedVersion.created_by_email && (
                  <Typography variant="body2">
                    <strong>Created by:</strong> {selectedVersion.created_by_email}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Reason:</strong> {selectedVersion.reason}
                </Typography>
                <Typography variant="body2">
                  <strong>Badges:</strong> {selectedVersion.badges?.join(', ') || 'None'}
                </Typography>
              </Box>

              {selectedVersion.file_path && (
                <Box textAlign="center" mt={2}>
                  <img 
                    src={`/api/images/file/${imageId}?version=${selectedVersion.id}`}
                    alt={`Version ${selectedVersion.version_number}`}
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ImageVersionHistory;